import React from 'react'
import styled from 'styled-components'
import { Text } from 'app/components/ui/Text'
import { ChatMessage, MessageData } from '.'
import { themeEstra } from 'app/styles/themeEstra'
import BlueChatIcon from 'static/icons/user_chat_blue.svg'
import BlackChatIcon from 'static/icons/user_chat_black.svg'
import { LoadingSpinner } from 'app/components/ui/LoadingSpinner'
import { FormFeedback } from 'app/components/ui/form/FormFeedback'
import { isImageFile } from 'app/utils/file'

interface Props {
  message: ChatMessage
  onMessageSent: () => void
  className?: string
}
export const Message: React.FC<Props> = ({
  className,
  message: { data, sendMessage },
  onMessageSent,
}) => {
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [error, setError] = React.useState<string>()
  const [messageData, setMessageData] = React.useState<MessageData>(data)

  const [isPreviewImageLoading, setPreviewImageLoading] = React.useState(true)

  const isYou = data ? data.isYou : true // Is you default to true, if not data is present yet

  const isTextMessage = data.type === 'text'
  const isImageFileMessage = isImageFile(messageData?.attachment_name)

  React.useEffect(() => {
    if (sendMessage) {
      setIsLoading(true)
      sendMessage()
        .then((res: MessageData) => {
          setIsLoading(false)
          setMessageData(res)
          onMessageSent()
        })
        .catch((error: Error) => {
          setError(error.message)
          setIsLoading(false)
        })
    }
  }, [sendMessage])

  return (
    <MainWrapper className={className} isYou={isYou}>
      <img src={isYou ? BlueChatIcon : BlackChatIcon} alt='user'></img>
      <MessageBox>
        <>
          <TitleWrapper>
            <Text variant='s14' weight='700'>
              {messageData?.username}
              {'  '}
              <MessageDataSpan>{messageData?.display_sent_at}</MessageDataSpan>
            </Text>
            <LoadingSpinnerWrapper>
              {isLoading && <LoadingSpinner size='small'></LoadingSpinner>}
            </LoadingSpinnerWrapper>
          </TitleWrapper>
          <Text variant='s14'>
            {isTextMessage ? (
              messageData?.message
            ) : isLoading ? (
              'Caricamento in corso...'
            ) : isImageFileMessage ? (
              <StyledLink
                target='_blank'
                rel='noopener noreferrer'
                href={messageData?.attachment_url}
              >
                <>
                  {isPreviewImageLoading && (
                    <PreviewImagePlaceholder> </PreviewImagePlaceholder>
                  )}
                  <PreviewImage
                    src={messageData?.attachment_url}
                    onLoad={() => setPreviewImageLoading(false)}
                    isLoading={isPreviewImageLoading}
                  ></PreviewImage>
                </>
              </StyledLink>
            ) : (
              <>
                Apri{' '}
                <StyledLink
                  target='_blank'
                  rel='noopener noreferrer'
                  href={messageData?.attachment_url}
                >
                  {messageData?.attachment_name || 'il File'}
                </StyledLink>
              </>
            )}
          </Text>
          {error && (
            <FormFeedback variant='error'>
              <Text variant='s12'>{error}</Text>
            </FormFeedback>
          )}
        </>
      </MessageBox>
    </MainWrapper>
  )
}

interface MainWrapperProps {
  isYou?: boolean
}

interface ImagePreview {
  isLoading?: boolean
}

const MainWrapper = styled.div<MainWrapperProps>`
  display: flex;
  margin: 2rem;

  @media (max-width: ${themeEstra.bp.desktop}) {
    margin: 1rem;
  }

  flex-direction: ${({ isYou }) => (isYou ? 'row-reverse' : null)};

  & > img {
    ${({ isYou }) => (isYou ? 'margin-left: 1rem;' : 'margin-right: 1rem;')}
  }

  & > div {
    background-color: ${({ isYou, theme }) =>
      isYou ? theme.color.bg.ciano3 : theme.color.neutri.grigioBg};
  }
`

const TitleWrapper = styled.div`
  display: flex;
  margin-bottom: 0.5rem;
  align-items: center;
`

const LoadingSpinnerWrapper = styled.div`
  width: 20px; //Same sizes of spinner small, needed to avoid flicker on loading
  height: 20px;
  margin-left: 0.5rem;
`

const MessageBox = styled.div`
  border-radius: 5px;
  padding: 0.5rem 1rem;

  word-break: break-word; //New line on long messages with no spaces
`

const MessageDataSpan = styled.span`
  font-weight: 400;
  color: ${({ theme }) => theme.color.primari.grigioEstra};
`
const StyledLink = styled.a`
  text-decoration: underline;
  cursor: pointer;
`

const PreviewImage = styled.img<ImagePreview>`
  width: 300px;
  max-height: 300px;
  object-fit: cover;
  display: ${({ isLoading }) => (isLoading ? 'none' : null)};
`

const PreviewImagePlaceholder = styled.div`
  width: 300px;
  height: 300px;
`
